// The story board editor's agent panel shows the session's conversation cut
// down (#1416, Adam 27 Sep): the last few turns only, the agent's replies as
// text, and each run of tool activity as one line. The full message list is a
// heavy thing to draw beside a page being edited; this is the part a reader
// editing a story board needs.
import { isImageToolCall, tryParseImageOutput, type AgentMessage, type SidePaneMessage, type ToolCallInfo } from '../../agent'

/** How many turns (a reader's message and what followed it) the panel shows. */
export const STORY_PANEL_TURNS = 3

/** An image a tool call produced. The panel draws it via the row's `extra`. */
export interface StoryPanelImage {
  base64: string
  mimeType: string
  name?: string
}

/** A row `storyPanelRows` draws. `image`, when set, is not on `SidePaneMessage`: the panel converts it to `extra` (an `<img>`), keyed on this row's own identity so the conversion stays memo-stable. */
export interface StoryPanelRow extends SidePaneMessage {
  image?: StoryPanelImage
}

// What "Ask the agent about this" sends (buildStoryAskPrompt): an optional
// element name line, then the element's source, then the instruction. The
// panel shows the instruction (and the name, when there is one), not the markup.
const ASK_PROMPT = /^(?:Story board element: (.+)\n)?Story board element \(in story\.html\):\n[\s\S]*\n\nInstruction: ([\s\S]*)$/

// Row objects are kept per source message, so a row whose message did not
// change keeps its identity and SidePaneChat's memoised rows do not redraw
// while another message streams.
const textRows = new WeakMap<AgentMessage, StoryPanelRow>()

function textRow(m: AgentMessage): StoryPanelRow {
  let row = textRows.get(m)
  if (!row) {
    const asked = m.role === 'user' ? ASK_PROMPT.exec(m.content) : null
    row = { id: m.id, role: m.role, content: asked ? `About ${asked[1] ? asked[1].trim() : 'the selected element'}: ${asked[2].trim()}` : m.content }
    textRows.set(m, row)
  }
  return row
}

/** The file name a tool call's image came from, when there is one worth showing. */
function imageName(tc: ToolCallInfo): string | undefined {
  const path = tc.input?.file_path as string | undefined
  return path ? path.split('/').pop() : undefined
}

// Cached per message, like textRow: a message whose tool calls did not
// change (already `complete`, so the reducer never touches it again) keeps
// its image rows' identity, so SidePaneChat's memoised rows do not redraw.
const imageRowsCache = new WeakMap<AgentMessage, StoryPanelRow[]>()

function imageRowsFor(m: AgentMessage): StoryPanelRow[] {
  let rows = imageRowsCache.get(m)
  if (!rows) {
    rows = []
    for (const tc of m.toolCalls ?? []) {
      if (!tc.output || !isImageToolCall(tc)) continue
      const img = tryParseImageOutput(tc.output)
      if (!img) continue
      rows.push({ id: `${m.id}:image:${tc.id}`, role: 'assistant', content: '', image: { base64: img.base64, mimeType: img.mimeType, name: imageName(tc) } })
    }
    imageRowsCache.set(m, rows)
  }
  return rows
}

export function toolStatusLine(count: number): string {
  return count === 1 ? 'Worked through 1 step' : `Worked through ${count} steps`
}

/**
 * The rows the panel draws: the last `turns` turns of `messages`. Tool calls
 * between two pieces of text become one status line, drawn with any images
 * they produced (`isImageToolCall`/`tryParseImageOutput`) right after it;
 * thinking is left out.
 */
export function storyPanelRows(messages: AgentMessage[], turns = STORY_PANEL_TURNS): StoryPanelRow[] {
  let start = 0
  let seen = 0
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      seen++
      start = i
      if (seen === turns) break
    }
  }
  const rows: StoryPanelRow[] = []
  let steps = 0
  let stepsId = ''
  let images: StoryPanelRow[] = []
  const flush = () => {
    if (steps > 0) rows.push({ id: `${stepsId}:steps:${steps}`, role: 'system', content: toolStatusLine(steps) })
    steps = 0
    if (images.length > 0) {
      rows.push(...images)
      images = []
    }
  }
  for (const m of messages.slice(start)) {
    // A message's words come before the tools it went on to call.
    if (m.content.trim()) {
      flush()
      rows.push(textRow(m))
    }
    if (m.role === 'assistant' && m.toolCalls?.length) {
      if (steps === 0) stepsId = m.id
      steps += m.toolCalls.length
      images.push(...imageRowsFor(m))
    }
  }
  flush()
  return rows
}
